// client/src/utils/matchReasons.ts
/**
 * D99: the ONE list of values a quotation line's catalogue-match label may hold.
 * `Quotation.js` validates against it and `catalogueMatch.matchDecisionFor`
 * writes from it; a code not listed here is refused at save.
 * Codes only, never prose (CLAUDE.md invariant 6).
 *
 * MIRRORED FILE: client/src/utils/matchReasons.ts is a line-for-line twin (the grid
 * words the same codes). Change one, change both; `mirroredUtilParity.test.js` checks.
 */
export const MATCH_DECISIONS = Object.freeze([
  'exact', 'confirmed', 'alternate_packaging', 'substitute', 'needs_confirmation',
  'rejected', 'unverified', 'not_attempted', 'legacy',
]);

export const MATCH_REASONS = Object.freeze([
  'low_name_score', 'size_differs', 'size_incompatible', 'size_unknown', 'pack_differs',
  'unit_unstated', 'dimension_code_differs', 'no_candidates', 'keyword_group',
  'single_strong_shortcut', 'ai_substitute', 'ai_verified', 'supplier_swap', 'preferred_vendor_swap',
  'ai_unavailable', 'catalogue_lookup_failed', 'historical_lookup_failed',
  'price_list_lookup_failed', 'yelo_lookup_failed', 'vendor_hints_unavailable', 'generation_error',
  'match_check_failed', 'historical_match_refused', 'price_list_match_refused', 'yelo_match_refused', 'human_confirmed', 'human_pick', 'human_alias', 'found_via_synonym', 'confirmation_dropped', 'remembered_match',
  'request_word_missing',
]);

/** Reasons only the matcher writes on a `confirmed` line (no person involved): such a line is never demoted. */
export const MACHINE_CONFIRMED_REASONS = Object.freeze(['keyword_group', 'ai_verified']);

/** Plain ops English for every reason code, shown on the grid. */
export const MATCH_REASON_LABELS: Readonly<Record<string, string>> = Object.freeze({
  low_name_score: 'Name is only a loose match',
  size_differs: 'Different size from what was asked',
  size_incompatible: 'Size cannot be compared with what was asked',
  size_unknown: 'Size not stated, could not be checked',
  pack_differs: 'Different pack from what was asked',
  unit_unstated: 'Catalogue item has no unit',
  dimension_code_differs: 'Different dimensions or code from what was asked',
  no_candidates: 'No catalogue item found',
  keyword_group: 'Generic request, matched to its keyword group',
  single_strong_shortcut: 'Only one strong candidate',
  ai_substitute: 'AI picked a substitute item',
  ai_verified: 'AI confirmed it is the same product',
  supplier_swap: 'Supplier was swapped',
  preferred_vendor_swap: 'Swapped to a preferred supplier',
  ai_unavailable: 'AI check was not available',
  catalogue_lookup_failed: 'Catalogue lookup failed',
  historical_lookup_failed: 'Past-quotation lookup failed',
  price_list_lookup_failed: 'Price-list lookup failed',
  yelo_lookup_failed: 'Yelo price lookup failed',
  vendor_hints_unavailable: 'Supplier hints not available',
  generation_error: 'Error while pricing this line',
  match_check_failed: 'The match could not be checked',
  historical_match_refused: 'A past quotation was found but is a different product',
  price_list_match_refused: 'A price-list item was found but is a different product',
  yelo_match_refused: 'A Yelo product was found but is a different product',
  human_confirmed: 'Confirmed by a person',
  human_pick: 'Picked by a person',
  confirmation_dropped: 'Earlier confirmation dropped: the catalogue item changed',
  human_alias: 'Matched through a name a person added',
  found_via_synonym: 'Found through a synonym',
  remembered_match: 'Remembered from an earlier confirmation',
  request_word_missing: 'Catalogue item lacks a word the request names',
});

/** Plain ops English for every decision code. */
export const MATCH_DECISION_LABELS: Readonly<Record<string, string>> = Object.freeze({
  exact: 'Exact match',
  confirmed: 'Confirmed',
  alternate_packaging: 'Different packaging',
  substitute: 'Substitute item',
  needs_confirmation: 'Needs confirmation',
  rejected: 'Rejected',
  unverified: 'Not verified',
  not_attempted: 'Not matched',
  legacy: 'Before match checks',
});

/** Decisions a person must look at before the line can be trusted. */
export const NEEDS_CONFIRMATION_DECISIONS = Object.freeze([
  'needs_confirmation', 'substitute', 'alternate_packaging', 'unverified',
]);

/** True while the line's match is flagged AND nobody has acknowledged it. */
export function needsConfirmation(
  item: { matchDecision?: string | null; matchAcknowledgedAt?: string | Date | null } | null | undefined
): boolean {
  if (!item) return false;
  return (NEEDS_CONFIRMATION_DECISIONS as readonly string[]).includes(item.matchDecision as string) && !item.matchAcknowledgedAt;
}

/** The ONE "this line has no usable price" rule (twin of the server's `lineIsUnpriced`). */
export function lineIsUnpriced(item: { unitPrice?: number | null } | null | undefined): boolean {
  return !(item && item.unitPrice !== null && item.unitPrice !== undefined && item.unitPrice > 0);
}

export interface QuotationReviewCounts { total: number; needsConfirmation: number; notInCatalogue: number; unpriced: number }

/**
 * A person added this line's item to the catalogue from the line: the pick mark with no row, until the
 * addition is approved. Nothing is left for a reviewer to answer (D179).
 */
export function addedToCatalogueFromLine(line: { pricingDetails?: unknown } | null | undefined): boolean {
  const details = line && line.pricingDetails;
  return !!(details && typeof details === 'object' && (details as Record<string, unknown>).catalogueSearchPick && !(details as Record<string, unknown>).masterCatalogueId);
}

/** What a reviewer has to look at on a quotation: one pass, one definition per number. */
export function quotationReviewCounts(
  items: ReadonlyArray<{ matchDecision?: string | null; matchAcknowledgedAt?: string | Date | null; notInCatalogue?: boolean | null; unitPrice?: number | null; pricingDetails?: unknown } | null | undefined> | null | undefined
): QuotationReviewCounts {
  const list = Array.isArray(items) ? items : [];
  const c: QuotationReviewCounts = { total: list.length, needsConfirmation: 0, notInCatalogue: 0, unpriced: 0 };
  for (const it of list) {
    if (lineIsUnpriced(it)) c.unpriced++;
    if (addedToCatalogueFromLine(it)) continue;
    if (needsConfirmation(it)) c.needsConfirmation++;
    if (it && it.notInCatalogue === true) c.notInCatalogue++;
  }
  return c;
}

/**
 * True when Confirm can succeed: the match is flagged and unacknowledged AND the line records the
 * catalogue row it was priced from (a person confirms a match TO a row). The server refuses the rest
 * with MATCH_NOT_CONFIRMABLE; the grid hides Confirm on them. Change match stays available.
 */
export function canConfirmMatch(
  item: { matchDecision?: string | null; matchAcknowledgedAt?: string | Date | null; pricingDetails?: unknown } | null | undefined
): boolean {
  const pd = item && item.pricingDetails;
  return needsConfirmation(item) && !!(pd && typeof pd === 'object' && (pd as Record<string, unknown>).masterCatalogueId);
}

/** Reasons only a person's own act writes: Confirm, or a catalogue row picked by hand. */
export const HUMAN_MATCH_REASONS = Object.freeze(['human_confirmed', 'human_pick']);

/**
 * A PERSON's match (D99 item 4): acknowledged, a human_* reason, or (D99.16) `confirmed` by the memory of a
 * person's earlier confirmation (`remembered_match`). A machine `confirmed` is none of these. D157.1: such a
 * line's Refresh re-prices its own row.
 */
export function hasHumanMatch(
  line: { matchDecision?: string | null; matchReasons?: readonly string[] | null; matchAcknowledgedAt?: string | Date | null } | null | undefined
): boolean {
  if (!line) return false;
  const reasons = Array.isArray(line.matchReasons) ? line.matchReasons : [];
  return !!line.matchAcknowledgedAt || reasons.some((r) => HUMAN_MATCH_REASONS.includes(r)) || (line.matchDecision === 'confirmed' && reasons.includes('remembered_match'));
}
